import { CellProps, useFocusManagement } from "components/TreeState"
import { PlanRecord, PlanRecordType } from "../types"
import { checkBuyingStatus } from "utils/type-guards"
import { isSemanticReadonly, usePlanCachedData } from "../utils"

import { CellError } from "./CellError"
import { FieldsKey } from "../components/AutoCompute/types"
import IconButton from "ui/buttons/IconButton"
import Input from "ui/inputs/Input"
import { ReactComponent as PriceTableIcon } from "assets/icon-pricetable.svg"
import { css } from "@emotion/css"
import { format as currencyFormat } from "utils/currencies"
import { get } from "lodash"
import { tableAggregations } from "../components/AutoCompute/useAggregations"
import { useAtomValue } from "jotai/utils"
import { useCellDialog } from "../useCellDialog"
import { useDefaultFieldIds } from "../useDefaultFieldIds"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"

export function RateCardCost(props: CellProps) {
  const { rowId: id, dataKey, columnId, disabled, primaryKey, semantic } = props
  const defaultFieldIds = useDefaultFieldIds(id)
  const tableSource = useTableSource()
  const aggregations = useAtomValue(tableAggregations)
  const readFrom = useSnapshot(tableSource.state)
  const readError = useSnapshot(tableSource.errors)
  const { onChange } = useCellDialog()

  const isBooking = primaryKey === "buyingMediaFields"
  const planData = usePlanCachedData(isBooking)

  const values = readFrom[id] || {}
  const { type, packaged } = values

  const buyingStatusVal = String(get(readFrom, `${id}.buyingStatus`))
  const buyingStatus = checkBuyingStatus(buyingStatusVal) ? buyingStatusVal : ""

  const mediaFields = readFrom[id]?.[
    primaryKey as keyof PlanRecord
  ] as PlanRecord["planningMediaFields"]

  const readonly = isSemanticReadonly(
    type as PlanRecordType,
    primaryKey as FieldsKey,
    semantic,
    packaged,
    defaultFieldIds,
    mediaFields,
  )

  const isMediaSubmedia =
    type === "media_type_plan" || type === "media_subtype_plan"

  const error = get(readError, `${id}.${columnId}`)
  const value = String(get(readFrom, `${id}.${dataKey}.defaultCost`) || "")
  const aggregatedValue = get(aggregations, [id, columnId]) ?? ""
  const hasAggregation = !!Object.keys(aggregations).includes(id)
  const appliedValue =
    isMediaSubmedia && hasAggregation && !isBooking && !packaged
      ? String(aggregatedValue ?? value)
      : value

  const fvalue = !!appliedValue
    ? currencyFormat(parseFloat(appliedValue), planData?.currencyCode, 2)
    : ""

  const { getInputProps } = useFocusManagement()
  const { onBlur } = getInputProps()

  const onOpenRateCard = () =>
    onChange({ type: "rate_card", fieldInfo: { rowId: id, dataKey } })

  const isDisabled = buyingStatus === "POSTED_FOR_BILLING"

  return readonly && type !== "line_item" ? (
    <span />
  ) : (
    <>
      <div className={styles.primaryInput}>
        <Input
          readOnly
          onBlur={onBlur}
          value={fvalue}
          autoComplete="off"
          type="tel"
          className={styles.input}
        />
        {!readonly ? (
          <IconButton
            className={styles.icon}
            disabled={isDisabled}
            onClick={() => !disabled && onOpenRateCard()}
          >
            <PriceTableIcon />
          </IconButton>
        ) : null}
      </div>
      {!!error ? <CellError error={error} /> : null}
    </>
  )
}

const styles = {
  icon: css`
    --btn-padding: var(--spacing-xxs);
    --btn-min-width: var(--spacing-xxl-2-1);
    &:focus {
      box-shadow: none;
    }
  `,
  primaryInput: css`
    display: flex;
    flex-direction: row;
    width: 100%;
    font-size: 13px;
  `,
  input: css`
    text-align: end;
  `,
}
